import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Check } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { SERVICES } from "../data/content";
import { scrollToId } from "../hooks/useLenis";

const ease = [0.16, 1, 0.3, 1];

const ServiceCard = ({ s, i, onOpen }) => {
  const Icon = s.icon;
  return (
    <motion.button
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.9, delay: (i % 3) * 0.1, ease }}
      whileHover={{ y: -6 }}
      onClick={() => onOpen(s)}
      data-testid={`service-card-${s.id}`}
      className={`group relative text-left rounded-[2rem] bg-white border border-forest/10 p-8 lg:p-10 overflow-hidden transition-shadow duration-500 hover:shadow-[0_40px_70px_-40px_rgba(26,79,50,0.45)] ${s.span}`}
    >
      <div className="absolute inset-0 bg-forest translate-y-[101%] group-hover:translate-y-0 transition-transform duration-500 ease-[cubic-bezier(.16,1,.3,1)]" />
      <div className="absolute -right-16 -bottom-16 w-56 h-56 rounded-full bg-african/0 group-hover:bg-african/30 blur-3xl transition-colors duration-700" />
      <div className="relative flex flex-col h-full min-h-[220px]">
        <div className="flex items-start justify-between">
          <div className="w-14 h-14 rounded-2xl bg-mist group-hover:bg-white/10 flex items-center justify-center transition-colors duration-500">
            <Icon className="w-7 h-7 text-forest group-hover:text-gold transition-colors duration-500" strokeWidth={1.8} />
          </div>
          <span className="font-accent text-sm text-forest/40 group-hover:text-white/50 transition-colors duration-500">0{i + 1}</span>
        </div>
        <h3 className="mt-8 font-heading font-bold text-2xl text-forest group-hover:text-white transition-colors duration-500">{s.title}</h3>
        <p className="mt-3 text-ink/65 group-hover:text-white/75 text-sm leading-relaxed transition-colors duration-500 max-w-md">{s.short}</p>
        <div className="mt-auto pt-8 inline-flex items-center gap-2 text-sm font-bold text-african group-hover:text-gold transition-colors duration-500">
          Découvrir <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:rotate-45" />
        </div>
      </div>
    </motion.button>
  );
};

export const Services = ({ onSelectService }) => {
  const [active, setActive] = useState(null);

  const askQuote = () => {
    onSelectService(active.title);
    setActive(null);
    setTimeout(() => scrollToId("contact"), 150);
  };

  return (
    <section id="services" className="relative bg-mist py-24 lg:py-32 overflow-hidden" data-testid="services-section">
      <div className="absolute inset-0 topo opacity-60" />
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-8 items-end mb-16">
          <div className="lg:col-span-8">
            <p className="eyebrow mb-5">Nos services</p>
            <h2 className="font-heading font-extrabold text-forest text-3xl sm:text-4xl lg:text-5xl leading-[1.02] tracking-tight">
              Six piliers pour une culture <span className="font-accent font-normal text-safety">sécurité</span> durable.
            </h2>
          </div>
          <p className="lg:col-span-4 text-ink/65 text-base leading-relaxed">
            Du diagnostic au terrain, nous bâtissons avec vous des environnements de travail où la performance ne se paie jamais en vies humaines.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6" data-testid="services-grid">
          {SERVICES.map((s, i) => <ServiceCard key={s.id} s={s} i={i} onOpen={setActive} />)}
        </div>
      </div>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="max-w-lg rounded-[2rem] p-8 sm:p-10 border-forest/10" data-testid="service-dialog">
          {active && (
            <>
              <DialogHeader className="text-left">
                <p className="eyebrow mb-2">Service</p>
                <DialogTitle className="font-heading font-extrabold text-2xl sm:text-3xl text-forest">{active.title}</DialogTitle>
                <DialogDescription className="text-ink/65 text-base pt-2">{active.short}</DialogDescription>
              </DialogHeader>
              <ul className="mt-6 space-y-3">
                {active.details.map((d) => (
                  <li key={d} className="flex items-start gap-3 text-sm text-ink/80">
                    <span className="mt-0.5 w-5 h-5 rounded-full bg-safety/15 flex items-center justify-center shrink-0"><Check className="w-3 h-3 text-safety" /></span>
                    {d}
                  </li>
                ))}
              </ul>
              <button onClick={askQuote} data-testid="service-dialog-cta" className="mt-8 w-full rounded-full bg-african hover:bg-forest text-white font-bold py-4 transition-colors duration-300">
                Demander ce service
              </button>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};
